/**
 * SmartFlowEditor - Editor principal de Smart Flows
 * Diseño: Light theme matching AdminCorrections pattern
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publishNaraContext, clearNaraContext } from "../naraChatContext";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Switch,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  AutoAwesome as SmartIcon,
  Person as PersonIcon,
  Storage as StorageIcon,
  PlayArrow as SimulatorIcon,
  Bolt as BoltIcon,
  SettingsBackupRestore as SettingsBackupRestoreIcon,
  Save as SaveIcon,
  Phone as PhoneIcon,
  Add as AddIcon,
  Check as CheckIcon,
  WhatsApp as WhatsAppIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { Avatar, Paper } from "@mui/material";

import { StyledDialog } from "../common";

import PersonalityTab from "./tabs/PersonalityTab";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab";
import SimulatorTab from "./tabs/SimulatorTab";
import IntentsTab from "./IntentsTab";
import SnapshotsDialog from "./SnapshotsDialog";

const API_URL = "/api";

// Background pattern (light theme)
const bgPattern = `
  radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.06) 0%, transparent 50%),
  radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.04) 0%, transparent 40%),
  radial-gradient(circle at 50% 80%, rgba(245, 158, 11, 0.03) 0%, transparent 40%)
`;

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`smartflow-tabpanel-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Avatar determinístico por nombre del chatbot (mismo patrón que Pacientes/SmartFlowsList)
const AVATAR_COLORS = [
  { bg: "#e0e7ff", fg: "#3730a3" }, { bg: "#fce7f3", fg: "#9f1239" },
  { bg: "#cffafe", fg: "#155e75" }, { bg: "#dcfce7", fg: "#166534" },
  { bg: "#fef3c7", fg: "#92400e" }, { bg: "#ede9fe", fg: "#5b21b6" },
  { bg: "#dbeafe", fg: "#1e40af" }, { bg: "#fee2e2", fg: "#991b1b" },
];

function avatarFromName(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const color = AVATAR_COLORS[h % AVATAR_COLORS.length];
  let inits = "?";
  if (name) {
    const parts = name.trim().split(/\s+/);
    inits = parts.length === 1
      ? parts[0].charAt(0).toUpperCase()
      : (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  return { ...color, inits };
}

export default function SmartFlowEditor() {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [activeTab, setActiveTab] = useState(0);
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);

  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [twilioNumbers, setTwilioNumbers] = useState([]);
  const [newNumber, setNewNumber] = useState("");
  const [savingNumber, setSavingNumber] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}`);
      if (!response.ok) throw new Error("Error cargando configuración");
      const data = await response.json();
      setConfig(data);
      // Publish chatbot context to NaraChat
      publishNaraContext('chatbot', {
        id: data.chatbot_id || chatbotId,
        nombre: data.chatbot_name || data.name,
        whatsapp: data.whatsapp_number,
        tipo: data.flow_type,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => {
    fetchConfig();
    return () => clearNaraContext();
  }, [fetchConfig]);

  const saveAgentConfig = async (updatedConfig) => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/agent-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedConfig),
      });
      if (!response.ok) throw new Error("Error guardando configuración");
      const data = await response.json();
      setConfig((prev) => ({ ...prev, agent_config: data.agent_config }));
      setHasChanges(false);
      setSnackbar({ open: true, message: "Guardado", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const toggleAgentEnabled = async () => {
    await saveAgentConfig({ enabled: !config.agent_config.enabled });
  };

  const updateSystemPrompt = (newPrompt) => {
    setConfig((prev) => ({
      ...prev,
      agent_config: { ...prev.agent_config, system_prompt: newPrompt },
    }));
    setHasChanges(true);
  };

  const refreshKnowledgeBase = () => fetchConfig();

  const fetchTwilioNumbers = async () => {
    try {
      const response = await fetch(`${API_URL}/smart-flows/twilio-numbers/all`);
      if (response.ok) setTwilioNumbers(await response.json());
    } catch (err) {
      console.error("Error cargando números Twilio:", err);
    }
  };

  const openWhatsappDialog = () => {
    fetchTwilioNumbers();
    setNewNumber("");
    setWhatsappDialogOpen(true);
  };

  const assignWhatsappNumber = async (numeroTwilio) => {
    try {
      setSavingNumber(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/assign-number`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ numero_twilio: numeroTwilio }),
      });
      if (!response.ok) throw new Error("Error asignando número");
      const result = await response.json();
      setSnackbar({ open: true, message: result.message, severity: "success" });
      setWhatsappDialogOpen(false);
      fetchConfig();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingNumber(false);
    }
  };

  const unassignWhatsappNumber = async () => {
    try {
      setSavingNumber(true);
      const response = await fetch(`${API_URL}/smart-flows/${chatbotId}/unassign-number`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Error desasignando número");
      setSnackbar({ open: true, message: "Número desasignado", severity: "success" });
      setWhatsappDialogOpen(false);
      fetchConfig();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setSavingNumber(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f1f5f9",
          backgroundImage: bgPattern,
        }}
      >
        <CircularProgress sx={{ color: "#6366f1" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "#f1f5f9", backgroundImage: bgPattern }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/smart-flows")} sx={{ color: "#1e293b" }}>
          Volver
        </Button>
      </Box>
    );
  }

  const isEnabled = config.agent_config?.enabled;
  const kbCount = config.knowledge_entries?.length || 0;
  const intentsCount = (config.agent_config?.tools_config?.intents || []).length;
  const hasConfig = !!config.agent_config?.system_prompt;
  const avatar = avatarFromName(config.chatbot_name);

  const TABS = [
    { id: "resumen",     label: "Resumen",             icon: <DashboardIcon sx={{ fontSize: 16 }} /> },
    { id: "personalidad", label: "Personalidad",       icon: <PersonIcon sx={{ fontSize: 16 }} /> },
    { id: "kb",          label: "Conocimiento",        icon: <StorageIcon sx={{ fontSize: 16 }} />, count: kbCount, countColor: "#0ea5e9" },
    { id: "intents",     label: "Intents",             icon: <BoltIcon sx={{ fontSize: 16 }} />, count: intentsCount, countColor: "#7c3aed" },
    { id: "simulador",   label: "Simulador",           icon: <SimulatorIcon sx={{ fontSize: 16 }} /> },
  ];

  // Estado de salud agregado
  const healthItems = [
    { ok: hasConfig, label: hasConfig ? "Personalidad configurada" : "Falta configurar personalidad", target: 1 },
    { ok: !!config.whatsapp_number, label: config.whatsapp_number ? "WhatsApp conectado" : "Sin número de WhatsApp", target: "whatsapp" },
    { ok: intentsCount > 0, label: intentsCount > 0 ? `${intentsCount} intent${intentsCount === 1 ? "" : "s"} activos` : "Sin intents configurados", target: 3 },
    { ok: kbCount > 0, label: kbCount > 0 ? `${kbCount} entradas en la base` : "Sin entradas en la base", target: 2 },
    { ok: isEnabled, label: isEnabled ? "Agente IA activo" : "Agente IA inactivo", target: null },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", pb: hasChanges ? 9 : 0 }}>
      {/* Breadcrumb + acciones */}
      <Box sx={{
        px: { xs: 2, md: 3 }, pt: 2, pb: 1,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 2, flexWrap: "wrap",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#64748b", fontSize: 12 }}>
          <Box
            onClick={() => navigate("/smart-flows")}
            sx={{
              display: "flex", alignItems: "center", gap: 0.4,
              cursor: "pointer", fontWeight: 600,
              "&:hover": { color: "#1e3a5f" },
            }}
          >
            <BackIcon sx={{ fontSize: 14 }} />
            Smart Flows
          </Box>
          <ChevronRightIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
          <Typography sx={{ fontSize: 12, color: "#1e3a5f", fontWeight: 700, maxWidth: { xs: 140, sm: 280, md: 400 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {config.chatbot_name}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Guarda copias del chatbot para volver atrás si algo sale mal">
            <Button
              size="small" variant="outlined"
              startIcon={<SettingsBackupRestoreIcon sx={{ fontSize: 16 }} />}
              onClick={() => setSnapshotsOpen(true)}
              sx={{
                color: "#4f46e5", borderColor: "#c7d2fe", bgcolor: "#fff",
                textTransform: "none", fontWeight: 600, fontSize: 12,
                borderRadius: 2, px: 1.5, py: 0.5,
                "&:hover": { bgcolor: "#eef2ff", borderColor: "#6366f1" },
              }}
            >
              Copias
            </Button>
          </Tooltip>
          <Tooltip title={isEnabled ? "Desactivar agente IA" : "Activar agente IA"}>
            <Box
              onClick={toggleAgentEnabled}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.55, borderRadius: 2, cursor: "pointer",
                bgcolor: isEnabled ? "#f0fdf4" : "#f8fafc",
                border: isEnabled ? "1px solid #bbf7d0" : "1px solid #e2e8f0",
                transition: "all 0.15s",
                "&:hover": { bgcolor: isEnabled ? "#dcfce7" : "#f1f5f9" },
              }}
            >
              <Switch checked={isEnabled} size="small"
                sx={{ width: 32, height: 18, p: 0,
                  "& .MuiSwitch-switchBase": { p: 0.25,
                    "&.Mui-checked": { transform: "translateX(14px)", color: "#22c55e",
                      "& + .MuiSwitch-track": { bgcolor: "#22c55e", opacity: 0.5 } } },
                  "& .MuiSwitch-thumb": { width: 14, height: 14 },
                  "& .MuiSwitch-track": { borderRadius: 9, bgcolor: "#cbd5e1" } }} />
              <Typography sx={{ color: isEnabled ? "#16a34a" : "#94a3b8", fontSize: 12, fontWeight: 700 }}>
                {isEnabled ? "Activo" : "Inactivo"}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* HERO CARD del chatbot */}
      <Box sx={{ px: { xs: 2, md: 3 }, mb: 1.5 }}>
        <Paper sx={{
          borderRadius: 3, p: { xs: 2, md: 2.5 },
          border: "1px solid #e2e8f0", background: "#fff",
          display: "flex", alignItems: { xs: "flex-start", sm: "center" },
          gap: 2, flexWrap: "wrap",
        }}>
          <Avatar sx={{
            width: 56, height: 56,
            bgcolor: avatar.bg, color: avatar.fg,
            fontSize: 20, fontWeight: 800, flexShrink: 0,
          }}>
            {avatar.inits}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.5, flexWrap: "wrap" }}>
              <Typography variant="h5" sx={{
                fontWeight: 700, color: "#1e3a5f", letterSpacing: -0.2,
                fontSize: { xs: 18, sm: 22 },
              }}>
                {config.chatbot_name}
              </Typography>
              <Chip
                size="small" label={isEnabled ? "Activo" : "Inactivo"}
                sx={{
                  height: 20, fontSize: 10.5, fontWeight: 700,
                  bgcolor: isEnabled ? "#dcfce7" : "#f1f5f9",
                  color: isEnabled ? "#15803d" : "#64748b",
                }}
              />
              {config.whatsapp_number ? (
                <Chip
                  size="small" label="WhatsApp"
                  icon={<WhatsAppIcon sx={{ fontSize: 12 }} />}
                  sx={{
                    height: 20, fontSize: 10.5, fontWeight: 700,
                    bgcolor: "#dcfce7", color: "#16a34a",
                    "& .MuiChip-icon": { color: "#16a34a", ml: 0.4 },
                  }}
                />
              ) : (
                <Chip
                  size="small" label="Sin WhatsApp"
                  icon={<WarningAmberIcon sx={{ fontSize: 12 }} />}
                  onClick={openWhatsappDialog}
                  sx={{
                    height: 20, fontSize: 10.5, fontWeight: 700,
                    bgcolor: "#fef3c7", color: "#92400e", cursor: "pointer",
                    "& .MuiChip-icon": { color: "#92400e", ml: 0.4 },
                    "&:hover": { bgcolor: "#fde68a" },
                  }}
                />
              )}
              {!hasConfig && (
                <Chip size="small" label="Sin configurar"
                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "#64748b", fontSize: 12.5, flexWrap: "wrap" }}>
              {config.whatsapp_number && (
                <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                  {config.whatsapp_number}
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <BoltIcon sx={{ fontSize: 13, color: "#7c3aed" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                  {intentsCount} intent{intentsCount === 1 ? "" : "s"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <StorageIcon sx={{ fontSize: 13, color: "#0ea5e9" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                  {kbCount} en base
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Tabs estilo segmented chips (consistente con SmartFlowsList / Pacientes) */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 1,
        display: "flex", flexWrap: "wrap", gap: 0.6,
        borderBottom: "1px solid #e2e8f0", background: "#fff",
      }}>
        {TABS.map((t, i) => {
          const activo = activeTab === i;
          return (
            <Chip
              key={t.id}
              icon={t.icon}
              label={
                t.count != null ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <span>{t.label}</span>
                    <Box sx={{
                      fontSize: 10, fontWeight: 700,
                      bgcolor: activo ? "rgba(255,255,255,0.25)" : "#e2e8f0",
                      color: activo ? "#fff" : "#475569",
                      px: 0.7, py: 0.05, borderRadius: 1,
                    }}>
                      {t.count}
                    </Box>
                  </Box>
                ) : t.label
              }
              onClick={() => setActiveTab(i)}
              sx={{
                fontSize: 12.5, fontWeight: 600, height: 32, cursor: "pointer",
                color: activo ? "#fff" : "#475569",
                background: activo ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)" : "#fff",
                border: activo ? "none" : "1px solid #e2e8f0",
                "& .MuiChip-icon": { color: activo ? "#fff" : "#94a3b8", ml: 0.8 },
                "&:hover": {
                  background: activo
                    ? "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)"
                    : "#f8fafc",
                },
              }}
            />
          );
        })}
      </Box>

      {/* Content area */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <TabPanel value={activeTab} index={0}>
          <ResumenTab
            config={config}
            chatbotId={chatbotId}
            isEnabled={isEnabled}
            kbCount={kbCount}
            intentsCount={intentsCount}
            healthItems={healthItems}
            onIrATab={setActiveTab}
            onConfigurarWhatsapp={openWhatsappDialog}
            onAbrirCopias={() => setSnapshotsOpen(true)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PersonalityTab
            config={config.agent_config}
            chatbotName={config.chatbot_name}
            onUpdate={updateSystemPrompt}
            onSave={(identityData) => saveAgentConfig({
              system_prompt: config.agent_config.system_prompt,
              ...identityData,
            })}
            saving={saving}
            hasChanges={hasChanges}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <KnowledgeBaseTab
            chatbotId={chatbotId}
            entries={config.knowledge_entries || []}
            categories={config.kb_categories || []}
            onRefresh={refreshKnowledgeBase}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <IntentsTab
            config={config.agent_config}
            saving={saving}
            onSave={(partial) => saveAgentConfig(partial)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <SimulatorTab
            chatbotId={chatbotId}
            chatbotName={config.chatbot_name}
            agentConfig={config.agent_config}
          />
        </TabPanel>
      </Box>

      {/* Sticky save bar (aparece solo si hay cambios sin guardar) */}
      {hasChanges && (
        <Paper sx={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          py: 1.5, px: { xs: 2, md: 3 },
          borderTop: "1px solid #c7d2fe",
          background: "linear-gradient(180deg, rgba(255,255,255,0.96), #fff)",
          boxShadow: "0 -4px 18px rgba(30, 58, 95, 0.08)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 2, zIndex: 1200, borderRadius: 0,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b",
              animation: "blink 1.4s ease-in-out infinite",
              "@keyframes blink": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.3 },
              },
            }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
              Tienes cambios sin guardar
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small" startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              onClick={() => { fetchConfig(); setHasChanges(false); }}
              sx={{
                color: "#64748b", textTransform: "none", fontWeight: 600,
                "&:hover": { bgcolor: "#f1f5f9" },
              }}
            >
              Descartar
            </Button>
            <Button
              size="small" variant="contained"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 14 }} />}
              onClick={() => saveAgentConfig({ system_prompt: config.agent_config.system_prompt })}
              disabled={saving}
              sx={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                textTransform: "none", fontWeight: 700, fontSize: 13,
                borderRadius: 2, px: 2.5, py: 0.75,
                boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
              }}
            >
              Guardar cambios
            </Button>
          </Box>
        </Paper>
      )}

      {/* Copias de seguridad */}
      <SnapshotsDialog
        open={snapshotsOpen}
        onClose={() => setSnapshotsOpen(false)}
        chatbotId={chatbotId}
        onRestored={() => { setSnapshotsOpen(false); fetchConfig(); }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* WhatsApp Dialog → StyledDialog */}
      <StyledDialog
        open={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        title="Configurar WhatsApp"
        icon={<WhatsAppIcon />}
        variant="compact"
        maxWidth="sm"
      >
        <StyledDialog.Body compact>
          {config?.whatsapp_number && (
            <Box
              sx={{
                p: 1.5,
                bgcolor: "rgba(34, 197, 94, 0.08)",
                border: "1px solid rgba(34, 197, 94, 0.2)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                  Número actual:
                </Typography>
                <Typography sx={{ fontWeight: 600, color: "#16a34a", fontSize: 14 }}>
                  {config.whatsapp_number}
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={unassignWhatsappNumber}
                disabled={savingNumber}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Desasignar
              </Button>
            </Box>
          )}

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "#1e3a5f" }}>
              Números disponibles:
            </Typography>

            {twilioNumbers.length === 0 ? (
              <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
                No hay números configurados.
              </Typography>
            ) : (
              <List sx={{ py: 0 }}>
                {twilioNumbers.map((num) => (
                  <ListItem key={num.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => assignWhatsappNumber(num.numero_twilio)}
                      disabled={savingNumber}
                      sx={{
                        borderRadius: 2,
                        py: 1,
                        bgcolor:
                          num.chatbot_id === parseInt(chatbotId)
                            ? "rgba(34, 197, 94, 0.08)"
                            : "rgba(0,0,0,0.02)",
                        border:
                          num.chatbot_id === parseInt(chatbotId)
                            ? "1px solid rgba(34, 197, 94, 0.2)"
                            : "1px solid rgba(0,0,0,0.06)",
                        "&:hover": { bgcolor: "rgba(30, 58, 95, 0.06)" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <PhoneIcon
                          sx={{
                            fontSize: 18,
                            color: num.chatbot_id === parseInt(chatbotId) ? "#22c55e" : "#94a3b8",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={num.numero_display || num.numero_twilio}
                        secondary={
                          num.chatbot_id
                            ? num.chatbot_id === parseInt(chatbotId)
                              ? "Asignado aquí"
                              : `Usado por: ${num.chatbot_name}`
                            : "Disponible"
                        }
                        primaryTypographyProps={{ color: "#1e293b", fontSize: 13, fontWeight: 500 }}
                        secondaryTypographyProps={{
                          color: num.chatbot_id ? "#f59e0b" : "#22c55e",
                          fontSize: 11,
                        }}
                      />
                      {num.chatbot_id === parseInt(chatbotId) && <CheckIcon sx={{ color: "#22c55e", fontSize: 18 }} />}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1, color: "#1e3a5f" }}>
              Agregar número:
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                placeholder="whatsapp:+573001234567"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: "#fff",
                    fontSize: 13,
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => assignWhatsappNumber(newNumber)}
                disabled={!newNumber || savingNumber}
                sx={{
                  bgcolor: "#1e3a5f",
                  "&:hover": { bgcolor: "#2d5a87" },
                  minWidth: 80,
                  fontSize: 12,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {savingNumber ? <CircularProgress size={16} color="inherit" /> : "Asignar"}
              </Button>
            </Box>
          </Box>
        </StyledDialog.Body>

        <StyledDialog.CompactActions>
          <StyledDialog.CancelButton fullWidth onClick={() => setWhatsappDialogOpen(false)}>
            Cerrar
          </StyledDialog.CancelButton>
        </StyledDialog.CompactActions>
      </StyledDialog>
    </Box>
  );
}

// ============================================================================
// ResumenTab — página de entrada del editor con métricas + accesos rápidos
// ============================================================================
function ResumenTab({
  config, chatbotId, isEnabled, kbCount, intentsCount, healthItems,
  onIrATab, onConfigurarWhatsapp, onAbrirCopias,
}) {
  const [rangoDias, setRangoDias] = useState(7);
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  useEffect(() => {
    if (!chatbotId) return;
    setLoadingMetrics(true);
    const userId = localStorage.getItem("user_id");
    const headers = userId ? { "X-User-Id": String(userId) } : {};
    fetch(`${API_URL}/chatbots/${chatbotId}/metrics?days=${rangoDias}`, { headers })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setMetrics(d))
      .catch(() => setMetrics(null))
      .finally(() => setLoadingMetrics(false));
  }, [chatbotId, rangoDias]);

  const STATS = [
    { label: "Intents activos",     value: intentsCount,                                 color: "#7c3aed", icon: <BoltIcon sx={{ fontSize: 18 }} /> },
    { label: "Entradas en base",    value: kbCount,                                       color: "#0ea5e9", icon: <StorageIcon sx={{ fontSize: 18 }} /> },
    { label: "Conversaciones",      value: config.conversaciones_totales || 0,            color: "#10b981", icon: <ChevronRightIcon sx={{ fontSize: 18 }} /> },
    { label: "Estado del agente",   value: isEnabled ? "Activo" : "Inactivo",             color: isEnabled ? "#16a34a" : "#94a3b8", icon: isEnabled ? <CheckCircleIcon sx={{ fontSize: 18 }} /> : <WarningAmberIcon sx={{ fontSize: 18 }} />, isText: true },
  ];

  const QUICK = [
    { label: "Editar personalidad",      sub: "System prompt + identidad",       icon: <PersonIcon sx={{ fontSize: 20 }} />,    color: "#6366f1", action: () => onIrATab(1) },
    { label: "Base de conocimiento",     sub: `${kbCount} entradas`,             icon: <StorageIcon sx={{ fontSize: 20 }} />,   color: "#0ea5e9", action: () => onIrATab(2) },
    { label: "Configurar intents",       sub: `${intentsCount} configurados`,    icon: <BoltIcon sx={{ fontSize: 20 }} />,      color: "#7c3aed", action: () => onIrATab(3) },
    { label: "Probar en simulador",      sub: "Chatea con el agente",            icon: <SimulatorIcon sx={{ fontSize: 20 }} />, color: "#10b981", action: () => onIrATab(4) },
    { label: "Copias de seguridad",      sub: "Guarda y restaura versiones",     icon: <SettingsBackupRestoreIcon sx={{ fontSize: 20 }} />, color: "#4f46e5", action: onAbrirCopias },
    { label: config.whatsapp_number ? "WhatsApp conectado" : "Conectar WhatsApp", sub: config.whatsapp_number || "Asigna un número", icon: <WhatsAppIcon sx={{ fontSize: 20 }} />, color: "#16a34a", action: onConfigurarWhatsapp },
  ];

  const healthOk = healthItems.filter((h) => h.ok).length;
  const healthTotal = healthItems.length;
  const healthPct = healthTotal > 0 ? Math.round((healthOk / healthTotal) * 100) : 0;
  const healthColor = healthPct >= 80 ? "#16a34a" : healthPct >= 50 ? "#f59e0b" : "#ef4444";

  return (
    <Box>
      {/* Métricas */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: 1.5, mb: 2.5,
      }}>
        {STATS.map((s) => (
          <Paper key={s.label} sx={{
            p: 2, borderRadius: 2.5,
            border: "1px solid #e2e8f0", background: "#fff",
            display: "flex", alignItems: "center", gap: 1.5,
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              background: s.color + "18", color: s.color,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {s.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 600,
                color: "#64748b", textTransform: "uppercase",
                letterSpacing: 0.4,
              }}>
                {s.label}
              </Typography>
              <Typography sx={{
                fontSize: s.isText ? 16 : 22, fontWeight: 800,
                color: "#1e3a5f", letterSpacing: -0.3, lineHeight: 1.1,
              }}>
                {s.value}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Mini-dashboard de actividad ── */}
      <ActividadCard
        metrics={metrics}
        loading={loadingMetrics}
        rangoDias={rangoDias}
        onChangeRango={setRangoDias}
      />

      {/* Estado de salud */}
      <Paper sx={{
        p: 2.5, borderRadius: 2.5, mb: 2.5,
        border: "1px solid #e2e8f0", background: "#fff",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f", letterSpacing: -0.1 }}>
              Estado de salud
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
              {healthOk} de {healthTotal} elementos configurados
            </Typography>
          </Box>
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: 2,
            background: healthColor + "18",
            color: healthColor,
            fontSize: 18, fontWeight: 800,
          }}>
            {healthPct}%
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
          {healthItems.map((h, i) => (
            <Box
              key={i}
              onClick={() => h.target != null && typeof h.target === "number" ? onIrATab(h.target) : (h.target === "whatsapp" && onConfigurarWhatsapp())}
              sx={{
                display: "flex", alignItems: "center", gap: 1, py: 0.5,
                cursor: h.target != null ? "pointer" : "default",
                "&:hover": h.target != null ? { "& .health-arrow": { transform: "translateX(2px)", color: "#1e3a5f" } } : {},
              }}
            >
              {h.ok
                ? <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                : <WarningAmberIcon sx={{ fontSize: 16, color: "#f59e0b" }} />}
              <Typography sx={{ flex: 1, fontSize: 12.5, color: h.ok ? "#475569" : "#92400e", fontWeight: 500 }}>
                {h.label}
              </Typography>
              {h.target != null && (
                <KeyboardArrowRightIcon className="health-arrow" sx={{
                  fontSize: 16, color: "#cbd5e1", transition: "all 0.15s",
                }} />
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Accesos rápidos */}
      <Typography sx={{
        fontSize: 11, fontWeight: 800, color: "#64748b",
        letterSpacing: 1.5, textTransform: "uppercase", mb: 1, mx: 0.5,
      }}>
        Accesos rápidos
      </Typography>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
        gap: 1.5,
      }}>
        {QUICK.map((q) => (
          <Paper
            key={q.label}
            onClick={q.action}
            sx={{
              p: 2, borderRadius: 2.5,
              border: "1px solid #e2e8f0", background: "#fff",
              cursor: "pointer", transition: "all 0.18s",
              display: "flex", alignItems: "center", gap: 1.5,
              "&:hover": {
                borderColor: q.color,
                boxShadow: `0 4px 12px ${q.color}22`,
                "& .qa-arrow": { transform: "translateX(3px)", color: q.color },
              },
            }}
          >
            <Box sx={{
              width: 40, height: 40, borderRadius: 2,
              background: q.color + "15", color: q.color,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              {q.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e3a5f", letterSpacing: -0.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {q.label}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {q.sub}
              </Typography>
            </Box>
            <KeyboardArrowRightIcon className="qa-arrow" sx={{
              fontSize: 18, color: "#cbd5e1", transition: "all 0.15s",
            }} />
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

// ============================================================================
// ActividadCard — mini-dashboard de métricas con sparkline SVG inline
// ============================================================================
function ActividadCard({ metrics, loading, rangoDias, onChangeRango }) {
  const RANGOS = [
    { value: 7,  label: "7 días" },
    { value: 30, label: "30 días" },
    { value: 90, label: "90 días" },
  ];

  const totales = metrics?.totales || {};
  const serie = metrics?.serie_diaria || [];
  const topTemas = metrics?.top_temas || [];
  const sinDatos = !loading && (totales.conversaciones || 0) === 0;

  return (
    <Paper sx={{
      p: 0, borderRadius: 2.5, mb: 2.5,
      border: "1px solid #e2e8f0", background: "#fff",
      overflow: "hidden",
    }}>
      {/* Header con toggle de rango */}
      <Box sx={{
        px: 2.5, py: 1.5,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 1, flexWrap: "wrap",
        borderBottom: "1px solid #f1f5f9",
      }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f", letterSpacing: -0.1 }}>
            Actividad
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
            Resumen de las conversaciones reales del chatbot
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, p: 0.4, borderRadius: 2, background: "#f1f5f9" }}>
          {RANGOS.map((r) => (
            <Box
              key={r.value}
              onClick={() => onChangeRango(r.value)}
              sx={{
                px: 1.25, py: 0.4, borderRadius: 1.5,
                fontSize: 11.5, fontWeight: rangoDias === r.value ? 700 : 600,
                color: rangoDias === r.value ? "#fff" : "#64748b",
                background: rangoDias === r.value
                  ? "linear-gradient(135deg, #1e3a5f, #2d5a87)"
                  : "transparent",
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { color: rangoDias === r.value ? "#fff" : "#1e3a5f" },
              }}
            >
              {r.label}
            </Box>
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: "#1e3a5f" }}/>
        </Box>
      ) : sinDatos ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>
            Sin actividad en los últimos {rangoDias} días
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.3 }}>
            Cuando lleguen conversaciones aparecerán acá las métricas.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ p: 2.5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 2.5 }}>
          {/* Sparkline + KPIs */}
          <Box>
            <Sparkline serie={serie} />
            <Box sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
              gap: 1.25, mt: 2,
            }}>
              <Kpi
                label="Conversaciones"
                value={totales.conversaciones || 0}
                color="#1e3a5f"
              />
              <Kpi
                label="Completadas"
                value={`${totales.tasa_completadas_pct || 0}%`}
                sub={`${totales.completadas || 0} de ${totales.conversaciones || 0}`}
                color="#16a34a"
              />
              <Kpi
                label="Escalación"
                value={`${totales.tasa_escalacion_pct || 0}%`}
                sub={`${totales.escaladas || 0} escaladas`}
                color={(totales.tasa_escalacion_pct || 0) > 15 ? "#dc2626" : "#f59e0b"}
              />
              <Kpi
                label="Duración prom."
                value={fmtDuracion(totales.duracion_promedio_seg)}
                sub={`${totales.mensajes_promedio || 0} msg / conv.`}
                color="#0ea5e9"
              />
            </Box>
          </Box>

          {/* Top temas */}
          <Box>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, color: "#64748b",
              textTransform: "uppercase", letterSpacing: 0.6, mb: 1,
            }}>
              Temas más consultados
            </Typography>
            {topTemas.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                Sin temas detectados aún
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85 }}>
                {topTemas.map((t, i) => {
                  const max = Math.max(...topTemas.map((x) => x.count));
                  const pct = max > 0 ? Math.round((t.count / max) * 100) : 0;
                  return (
                    <Box key={i}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", textTransform: "capitalize" }}>
                          {t.tema}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#1e3a5f" }}>
                          {t.count}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 6, borderRadius: 3, background: "#f1f5f9", overflow: "hidden" }}>
                        <Box sx={{
                          width: `${pct}%`, height: "100%",
                          background: "linear-gradient(90deg, #1e3a5f, #2d5a87)",
                          transition: "width 0.4s ease",
                        }}/>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, color: "#94a3b8",
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color, letterSpacing: -0.3, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.15 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

// Gráfico de barras verticales con tooltip al hover — fácil de interpretar
// para conteos diarios. Stacked: conversaciones (navy) + escalaciones (rojo
// encima de la barra del mismo día).
function Sparkline({ serie }) {
  const [hover, setHover] = useState(null);  // índice de la barra hovered
  if (!serie || serie.length === 0) return null;

  const max = Math.max(1, ...serie.map((d) => d.conversaciones));
  const totalConvs = serie.reduce((s, d) => s + (d.conversaciones || 0), 0);
  const diaPico = serie.reduce((acc, d) => d.conversaciones > (acc?.conversaciones || 0) ? d : acc, null);

  return (
    <Box>
      {/* Etiqueta superior: total + pico */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.85 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Conversaciones por día
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
            · total {totalConvs}
          </Typography>
        </Box>
        {diaPico && diaPico.conversaciones > 0 && (
          <Typography sx={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>
            Pico: <Box component="span" sx={{ color: "#1e3a5f", fontWeight: 800 }}>{diaPico.conversaciones}</Box> el {fmtFechaCorta(diaPico.fecha)}
          </Typography>
        )}
      </Box>

      {/* Barras */}
      <Box sx={{
        position: "relative",
        display: "flex", alignItems: "flex-end",
        gap: serie.length > 30 ? "1px" : "2px",
        height: 110, px: 0.5,
        borderBottom: "1px solid #e2e8f0",
        background: "linear-gradient(180deg, transparent 0%, transparent 75%, #fafbfc 100%)",
      }}>
        {/* Gridlines horizontales sutiles */}
        {[0.25, 0.5, 0.75].map((p) => (
          <Box key={p} sx={{
            position: "absolute", left: 0, right: 0, bottom: `${p * 100}%`,
            height: "1px", background: "#f1f5f9", pointerEvents: "none",
          }}/>
        ))}

        {serie.map((d, i) => {
          const total = d.conversaciones || 0;
          const esc = d.escalaciones || 0;
          const conv = Math.max(0, total - esc);  // no-escaladas
          const altoTotal = total > 0 ? (total / max) * 100 : 0;
          const altoConv = total > 0 ? (conv / max) * 100 : 0;
          const altoEsc = total > 0 ? (esc / max) * 100 : 0;
          const isHover = hover === i;

          return (
            <Box
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              sx={{
                flex: 1, minWidth: 2,
                height: "100%",
                display: "flex", flexDirection: "column-reverse",
                cursor: "pointer", position: "relative",
              }}
            >
              {/* Barra principal */}
              {altoConv > 0 && (
                <Box sx={{
                  height: `${altoConv}%`,
                  background: isHover ? "#2d5a87" : "#1e3a5f",
                  borderRadius: esc > 0 ? "0" : "2px 2px 0 0",
                  transition: "background 0.15s",
                  minHeight: total > 0 ? 2 : 0,
                }}/>
              )}
              {/* Sección escalación (encima) */}
              {altoEsc > 0 && (
                <Box sx={{
                  height: `${altoEsc}%`,
                  background: isHover ? "#dc2626" : "#ef4444",
                  borderRadius: "2px 2px 0 0",
                  transition: "background 0.15s",
                  minHeight: 2,
                }}/>
              )}
              {/* Día sin actividad: barrita gris muy fina */}
              {total === 0 && (
                <Box sx={{
                  height: "2px",
                  background: isHover ? "#cbd5e1" : "#e2e8f0",
                  borderRadius: "1px",
                  transition: "background 0.15s",
                }}/>
              )}

              {/* Tooltip al hover */}
              {isHover && (
                <Box sx={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  left: "50%", transform: "translateX(-50%)",
                  px: 1, py: 0.5, borderRadius: 1.5,
                  background: "#1e3a5f", color: "#fff",
                  fontSize: 10.5, fontWeight: 600,
                  whiteSpace: "nowrap",
                  zIndex: 10,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  pointerEvents: "none",
                  "&::after": {
                    content: '""',
                    position: "absolute", top: "100%", left: "50%",
                    transform: "translateX(-50%)",
                    border: "4px solid transparent", borderTopColor: "#1e3a5f",
                  },
                }}>
                  <Box sx={{ fontSize: 10, opacity: 0.7, mb: 0.2 }}>{fmtFechaTooltip(d.fecha)}</Box>
                  <Box>{total} conversación{total === 1 ? "" : "es"}</Box>
                  {esc > 0 && (
                    <Box sx={{ color: "#fca5a5", fontSize: 10 }}>{esc} escalada{esc === 1 ? "" : "s"}</Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Labels de fecha */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
          {fmtFechaCorta(serie[0]?.fecha)}
        </Typography>
        {serie.length > 14 && (
          <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
            {fmtFechaCorta(serie[Math.floor(serie.length / 2)]?.fecha)}
          </Typography>
        )}
        <Typography sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>
          Hoy
        </Typography>
      </Box>

      {/* Leyenda */}
      <Box sx={{ display: "flex", gap: 1.5, mt: 1, justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.4, background: "#1e3a5f" }}/>
          <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>Conversaciones</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.4, background: "#ef4444" }}/>
          <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>Escaladas</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function fmtFechaTooltip(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short" });
}

function fmtDuracion(seg) {
  if (!seg) return "—";
  if (seg < 60) return `${seg}s`;
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function fmtFechaCorta(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}
